import React, { createContext, useContext } from 'react';
import { useModules } from '@/hooks/useApi';

interface ModuleContextValue {
  enabledModules: Set<string>;
  isLoading: boolean;
  isError: boolean;
  hasModule: (name: string) => boolean;
  /** On this instance at all, granted or not; undefined when the server doesn't say (older servers). */
  isOnInstance: (name: string) => boolean | undefined;
}

const ModuleContext = createContext<ModuleContextValue>({
  enabledModules: new Set(),
  isLoading: true,
  isError: false,
  hasModule: () => false,
  isOnInstance: () => undefined,
});

export const ModuleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data, isLoading, isError } = useModules();
  // R18: /api/modules lists what THIS user can open. 'reconciliation' (core sign-off) is in ``access`` when
  // granted; an older server without ``access`` means everything core is open, as before.
  const enabledModules = new Set(data?.enabled.map(m => m.name) ?? []);
  if (!data?.access || data.access.includes('reconciliation')) enabledModules.add('reconciliation');
  const hasModule = (name: string) => enabledModules.has(name);
  const isOnInstance = (name: string) => data?.available ? data.available.includes(name) : undefined;

  return (
    <ModuleContext.Provider value={{ enabledModules, isLoading, isError, hasModule, isOnInstance }}>
      {children}
    </ModuleContext.Provider>
  );
};

export const useModuleContext = () => useContext(ModuleContext);
